import React, { useState, useCallback } from "react";
import {
    Box, Typography, Paper, IconButton, Tooltip, Snackbar, Divider, Collapse
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { axios_tenant } from "../../../../axios";

const BASE_URL = "/collaboration/workflow";

/**
 * Email Draft Section shown on completed tasks for Admin/AM/QA.
 * Displays the generated email draft with a copy button.
 * Copies both HTML (for Outlook) and plain text to clipboard.
 * Collapsed by default.
 */
const EmailDraftSection = ({ taskId }) => {
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [copySnackbar, setCopySnackbar] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    React.useEffect(() => {
        if (!taskId) return;
        const fetchDraft = async () => {
            try {
                setLoading(true);
                const res = await axios_tenant.get(`${BASE_URL}/tasks/${taskId}/email-draft`);
                setDraft(res.data.data);
                setError(null);
            } catch (err) {
                if (err?.response?.status === 404) {
                    setDraft(null);
                } else {
                    setError("Failed to load email draft");
                }
            } finally {
                setLoading(false);
                setLoaded(true);
            }
        };
        fetchDraft();
    }, [taskId]);

    /**
     * Convert the plain-text body to HTML for Outlook-compatible clipboard copy.
     * Handles: markdown links [text](url), numbered lists, bulleted lists, plain text.
     */
    const convertBodyToHtml = useCallback((body) => {
        if (!body) return "";
        const lines = body.split('\n');
        let html = '';
        let inOl = false;
        let inUl = false;

        // Convert markdown links [text](url) and raw URLs to <a> tags
        const linkify = (text) => {
            return text
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
                .replace(/(?<!href=")(https?:\/\/[^\s<]+|www\.[^\s<]+)/g, (url) => {
                    const href = url.startsWith('http') ? url : `https://${url}`;
                    return `<a href="${href}">${url}</a>`;
                });
        };

        for (const line of lines) {
            const trimmed = line.trim();

            if (/^\d+\.\s/.test(trimmed)) {
                if (inUl) { html += '</ul>'; inUl = false; }
                if (!inOl) { html += '<ol>'; inOl = true; }
                html += `<li>${linkify(trimmed.replace(/^\d+\.\s*/, ''))}</li>`;
                continue;
            }

            if (/^[•\-]\s/.test(trimmed)) {
                if (inOl) { html += '</ol>'; inOl = false; }
                if (!inUl) { html += '<ul>'; inUl = true; }
                html += `<li>${linkify(trimmed.replace(/^[•\-]\s*/, ''))}</li>`;
                continue;
            }

            if (inOl) { html += '</ol>'; inOl = false; }
            if (inUl) { html += '</ul>'; inUl = false; }

            if (trimmed === '') {
                html += '<br>';
            } else {
                html += `<p style="margin:0 0 2px 0">${linkify(trimmed)}</p>`;
            }
        }

        if (inOl) html += '</ol>';
        if (inUl) html += '</ul>';

        return html;
    }, []);

    const handleCopy = useCallback(async () => {
        if (!draft?.body) return;
        try {
            const html = convertBodyToHtml(draft.body);
            const htmlBlob = new Blob([html], { type: 'text/html' });
            const textBlob = new Blob([draft.body], { type: 'text/plain' });
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': htmlBlob,
                    'text/plain': textBlob
                })
            ]);
            setCopySnackbar(true);
        } catch {
            await navigator.clipboard.writeText(draft.body);
            setCopySnackbar(true);
        }
    }, [draft, convertBodyToHtml]);

    /**
     * Parse a line of text, converting [text](url) and raw URLs to React <a> elements.
     */
    const renderLineParts = (text) => {
        // Split on markdown links [text](url)
        const parts = [];
        let remaining = text;
        const mdLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let match;
        let lastIndex = 0;

        while ((match = mdLinkRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            if (match[2].includes('/package/latest')) {
                parts.push(
                    <a key={`md-${match.index}`} href={match[2]} download style={{ color: '#2563EB', textDecoration: 'underline' }}>
                        {match[1]}
                    </a>
                );
            } else {
                parts.push(
                    <a key={`md-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'underline' }}>
                        {match[1]}
                    </a>
                );
            }
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        // Now handle raw URLs in text segments
        return parts.flatMap((part, i) => {
            if (typeof part !== 'string') return part;
            const urlParts = part.split(/(https?:\/\/[^\s]+|www\.[^\s]+)/g);
            return urlParts.map((seg, si) => {
                if (/^(https?:\/\/|www\.)/.test(seg)) {
                    const href = seg.startsWith('http') ? seg : `https://${seg}`;
                    if (href.includes('/package/latest')) {
                        return <a key={`url-${i}-${si}`} href={href} download style={{ color: '#2563EB' }}>{seg}</a>;
                    }
                    return <a key={`url-${i}-${si}`} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB' }}>{seg}</a>;
                }
                return seg;
            });
        });
    };

    /**
     * Render the body text with proper formatting for display.
     */
    const renderBody = (body) => {
        if (!body) return null;
        const lines = body.split('\n');
        const elements = [];
        let listItems = [];
        let listType = null;

        const flushList = () => {
            if (listItems.length > 0) {
                if (listType === 'ol') {
                    elements.push(
                        <ol key={`ol-${elements.length}`} style={{ margin: '8px 0', paddingLeft: 24 }}>
                            {listItems.map((item, i) => (
                                <li key={i} style={{ color: '#374151', lineHeight: 1.8 }}>{item}</li>
                            ))}
                        </ol>
                    );
                } else {
                    elements.push(
                        <ul key={`ul-${elements.length}`} style={{ margin: '8px 0', paddingLeft: 24 }}>
                            {listItems.map((item, i) => (
                                <li key={i} style={{ color: '#374151', lineHeight: 1.8 }}>{item}</li>
                            ))}
                        </ul>
                    );
                }
                listItems = [];
                listType = null;
            }
        };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmed = line.trim();

            if (/^\d+\.\s/.test(trimmed)) {
                if (listType !== 'ol') { flushList(); listType = 'ol'; }
                listItems.push(renderLineParts(trimmed.replace(/^\d+\.\s*/, '')));
                continue;
            }

            if (/^[•\-]\s/.test(trimmed)) {
                if (listType !== 'ul') { flushList(); listType = 'ul'; }
                listItems.push(renderLineParts(trimmed.replace(/^[•\-]\s*/, '')));
                continue;
            }

            flushList();

            if (trimmed === '') {
                elements.push(<Box key={`br-${i}`} sx={{ height: 8 }} />);
            } else {
                elements.push(
                    <Typography key={`p-${i}`} variant="body2" sx={{ color: '#374151', lineHeight: 1.8, mb: 0.25 }}>
                        {renderLineParts(trimmed)}
                    </Typography>
                );
            }
        }
        flushList();
        return elements;
    };

    if (!loaded || loading) return null;
    if (error) return <Typography color="error" variant="body2">{error}</Typography>;
    if (!draft) return null;

    return (
        <Box data-testid="oa-email-draft-section">
            <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: collapsed ? 0 : 2, cursor: 'pointer' }}
                onClick={() => setCollapsed(prev => !prev)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography fontWeight={600} sx={{ color: '#1a1a2e', fontSize: '16px' }}>
                        Email Draft
                    </Typography>
                    {collapsed ? <ExpandMoreIcon sx={{ color: '#64748B' }} /> : <ExpandLessIcon sx={{ color: '#64748B' }} />}
                </Box>
            </Box>

            <Collapse in={!collapsed}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        borderColor: '#E2E8F0',
                        backgroundColor: '#FAFBFC',
                        position: 'relative'
                    }}
                >
                    {/* Copy button inside content box */}
                    <Tooltip title="Copy email draft to clipboard">
                        <IconButton
                            onClick={handleCopy}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                border: '1px solid #E2E8F0',
                                borderRadius: 1.5,
                                bgcolor: '#fff',
                                '&:hover': { bgcolor: '#F1F5F9' }
                            }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 18, color: '#64748B' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Email Body */}
                    <Box>
                        {renderBody(draft.body)}
                    </Box>
                </Paper>
            </Collapse>

            <Snackbar
                open={copySnackbar}
                autoHideDuration={2000}
                onClose={() => setCopySnackbar(false)}
                message="Email draft copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default EmailDraftSection;
